import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { sanityClient } from "../lib/sanity/client";
import { urlForImage } from "../lib/sanity/image";
import "./ProductDetail.css";

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const query = `*[_type == "product" && slug.current == "${slug}"][0] {
        _id,
        title,
        slug,
        sku,
        description,
        price,
        priceUSD,
        originalValue,
        originalValueUSD,
        condition,
        status,
        images[] {
          asset->,
          alt
        },
        videoUrl,
        category-> {
          name,
          slug,
          description
        },
        tags,
        pawnDetails,
        featured,
        createdAt
      }`;

      const productData = await sanityClient.fetch(query);
      setProduct(productData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching product:", error);
      setLoading(false);
    }
  };

  // ✅ Convert YouTube/Vimeo URLs to embed format
  const getEmbeddableVideoUrl = (url) => {
    if (!url) return url;

    try {
      // YouTube Shorts
      const shortsRegex =
        /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/;
      const shortsMatch = url.match(shortsRegex);

      if (shortsMatch && shortsMatch[1]) {
        return `https://www.youtube.com/embed/${shortsMatch[1]}`;
      }

      // YouTube normal links
      const youtubeRegex =
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
      const match = url.match(youtubeRegex);

      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }

      // Vimeo URLS
      const vimeoRegex = /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/)([0-9]+)/;
      const vimeoMatch = url.match(vimeoRegex);

      if (vimeoMatch && vimeoMatch[1]) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
      }

      // Original
      return url;
    } catch (error) {
      console.error("Error processing video URL:", error);
      return url;
    }
  };

  const handleWhatsAppContact = () => {
    if (!product) return;

    // Create message with both currencies if available
    let priceText = `₦${product.price?.toLocaleString()}`;
    if (product.priceUSD) {
      priceText += ` ($${product.priceUSD?.toLocaleString()})`;
    }

    const message = `I want to get ${product.title} - ${priceText}`;
    const whatsappUrl = `https://wa.me/2349114351195?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleShareProduct = () => {
    let priceText = `₦${product.price?.toLocaleString()}`;
    if (product.priceUSD) {
      priceText += ` ($${product.priceUSD?.toLocaleString()})`;
    }

    if (navigator.share) {
      navigator.share({
        title: product.title,
        text: `Check out this product: ${product.title} - ${priceText}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Product link copied to clipboard!");
    }
  };

  if (loading) {
    return <div className="awoof-product-loading">Loading product...</div>;
  }

  if (!product) {
    return (
      <div className="awoof-product-not-found">
        <h2>Product not found</h2>
        <p>The product you're looking for doesn't exist.</p>
        <button onClick={() => (window.location.href = "/marketplace")}>
          Back to Marketplace
        </button>
      </div>
    );
  }

  // Calculate savings for both currencies
  const savingsNGN = product.originalValue
    ? product.originalValue - product.price
    : 0;
  const savingsPercentNGN = product.originalValue
    ? Math.round((savingsNGN / product.originalValue) * 100)
    : 0;

  const savingsUSD =
    product.originalValueUSD && product.priceUSD
      ? product.originalValueUSD - product.priceUSD
      : 0;
  const savingsPercentUSD =
    product.originalValueUSD && product.priceUSD
      ? Math.round((savingsUSD / product.originalValueUSD) * 100)
      : 0;

  // Pick first product image for SEO preview
  const seoImage =
    product.images?.[0] &&
    urlForImage(product.images[0]).width(600).height(600).url();

  return (
    <div className="awoof-product-container">
      <Helmet>
        <title>{`${product.title} | Respected Solution`}</title>
        <meta name="description" content={product.description} />
        <meta name="keywords" content={product.tags?.join(", ") ?? ""} />

        <meta property="og:title" content={product.title} />
        <meta property="og:description" content={product.description} />
        {seoImage && <meta property="og:image" content={seoImage} />}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={window.location.href} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={product.title} />
        <meta name="twitter:description" content={product.description} />
        {seoImage && <meta name="twitter:image" content={seoImage} />}
      </Helmet>

      <div className="awoof-product-grid">
        <div className="awoof-product-images">
          <div className="awoof-product-main-image">
            <img
              src={
                product.images?.[selectedImage]
                  ? urlForImage(product.images[selectedImage])
                      .width(600)
                      .height(600)
                      .url()
                  : "/images/placeholder-product.jpg"
              }
              alt={product.images?.[selectedImage]?.alt || product.title}
            />
          </div>

          {product.images && product.images.length > 1 && (
            <div className="awoof-product-image-thumbnails">
              {product.images.map((image, index) => (
                <img
                  key={index}
                  src={urlForImage(image).width(100).height(100).url()}
                  alt={image.alt || `${product.title} ${index + 1}`}
                  className={
                    selectedImage === index ? "awoof-product-thumb-active" : ""
                  }
                  onClick={() => setSelectedImage(index)}
                />
              ))}
            </div>
          )}

          {product.videoUrl && (
            <div className="awoof-product-video">
              <iframe
                src={getEmbeddableVideoUrl(product.videoUrl)}
                title={product.title}
                frameBorder="0"
                allowFullScreen
              />
            </div>
          )}
        </div>

        <div className="awoof-product-details">
          <div className="awoof-product-header">
            <h1>{product.title}</h1>
            {product.sku && (
              <span className="awoof-product-sku">SKU: {product.sku}</span>
            )}
            {product.featured && (
              <span className="awoof-product-featured-label">Featured</span>
            )}
          </div>

          <div className="awoof-product-price-section">
            <div className="awoof-product-price-group">
              <span className="awoof-product-current-price">
                ₦{product.price?.toLocaleString()}
              </span>
              {product.priceUSD && (
                <span className="awoof-product-current-price-usd">
                  ${product.priceUSD?.toLocaleString()}
                </span>
              )}
            </div>

            {(product.originalValue || product.originalValueUSD) && (
              <div className="awoof-product-original-pricing">
                {product.originalValue && (
                  <span className="awoof-product-original-price">
                    ₦{product.originalValue.toLocaleString()}
                  </span>
                )}
                {product.originalValueUSD && (
                  <span className="awoof-product-original-price">
                    ${product.originalValueUSD.toLocaleString()}
                  </span>
                )}
              </div>
            )}

            {(savingsNGN > 0 || savingsUSD > 0) && (
              <div className="awoof-product-savings-group">
                {savingsNGN > 0 && (
                  <span className="awoof-product-savings">
                    Save ₦{savingsNGN.toLocaleString()} ({savingsPercentNGN}%)
                  </span>
                )}
                {savingsUSD > 0 && (
                  <span className="awoof-product-savings">
                    Save ${savingsUSD.toLocaleString()} ({savingsPercentUSD}%)
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="awoof-product-status-section">
            <span
              className={`awoof-product-status ${product.status?.toLowerCase()}`}
            >
              {product.status}
            </span>
            <span
              className={`awoof-product-condition ${product.condition
                ?.toLowerCase()
                .replace(" ", "-")}`}
            >
              {product.condition}
            </span>
          </div>

          <div className="awoof-product-description-section">
            <h3>Description</h3>
            <p>{product.description}</p>
          </div>

          {product.pawnDetails && (
            <div className="awoof-product-pawn-details">
              <h3>Additional Details</h3>
              <p>{product.pawnDetails}</p>
            </div>
          )}

          {product.tags && product.tags.length > 0 && (
            <div className="awoof-product-tags-section">
              <h3>Tags</h3>
              <div className="awoof-product-tags-list">
                {product.tags.map((tag, index) => (
                  <span key={index} className="awoof-product-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="awoof-product-actions">
            <button
              className="awoof-product-btn-primary awoof-product-btn-large"
              onClick={handleWhatsAppContact}
            >
              Contact Us About This Item
            </button>
            <button
              className="awoof-product-btn-secondary"
              onClick={handleShareProduct}
            >
              Share Product
            </button>
            <button
              className="awoof-product-btn-secondary"
              onClick={() => (window.location.href = "/marketplace")}
            >
              Back to Marketplace
            </button>
          </div>

          <div className="awoof-product-meta">
            <p>
              <strong>Category:</strong> {product.category?.name}
            </p>
            <p>
              <strong>Listed:</strong>{" "}
              {new Date(product.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
